import { adminPasskeyAuthController, adminPasskeyController } from '@/api/generated/endpoints';
import type { AdminPasskeyResponseDTO, TokenDTO } from '@/api/generated/types';
import { unwrap } from '@/api/http';

/**
 * Passkeys: the browser's WebAuthn API around the gateway's passkey endpoints. The gateway sends
 * the options as WebAuthn JSON (binary fields in base64url) and takes the credential back the same
 * way; this converts between that and the ArrayBuffers the browser API uses.
 */

/** Whether this browser can use passkeys at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials;
}

/** The person closed or cancelled the browser's passkey prompt: not an error to show. */
export function isPasskeyCancel(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

function fromB64url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

function toB64url(buf: ArrayBuffer | null | undefined): string | undefined {
  if (!buf) return undefined;
  let bin = '';
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface CredentialDescriptorJSON {
  id: string;
  type: PublicKeyCredentialType;
  transports?: AuthenticatorTransport[];
}

function descriptors(list?: CredentialDescriptorJSON[]): PublicKeyCredentialDescriptor[] | undefined {
  return list?.map((c) => ({ ...c, id: fromB64url(c.id) }));
}

/** The gateway's creation options ({publicKey: …} in WebAuthn JSON) for navigator.credentials.create. */
export function creationOptions(json: unknown): CredentialCreationOptions {
  const pk = (json as { publicKey: Record<string, unknown> }).publicKey;
  const user = pk.user as { id: string; name: string; displayName: string };
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialCreationOptions),
      challenge: fromB64url(pk.challenge as string),
      user: { ...user, id: fromB64url(user.id) },
      excludeCredentials: descriptors(pk.excludeCredentials as CredentialDescriptorJSON[] | undefined),
    },
  };
}

/** The gateway's request options ({publicKey: …} in WebAuthn JSON) for navigator.credentials.get. */
export function requestOptions(json: unknown): CredentialRequestOptions {
  const pk = (json as { publicKey: Record<string, unknown> }).publicKey;
  return {
    publicKey: {
      ...(pk as unknown as PublicKeyCredentialRequestOptions),
      challenge: fromB64url(pk.challenge as string),
      allowCredentials: descriptors(pk.allowCredentials as CredentialDescriptorJSON[] | undefined),
    },
  };
}

/** A new passkey (navigator.credentials.create) as WebAuthn JSON. */
export function attestationJSON(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: toB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: toB64url(r.clientDataJSON),
      attestationObject: toB64url(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : undefined,
    },
  };
}

/** A passkey sign-in (navigator.credentials.get) as WebAuthn JSON. */
export function assertionJSON(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: toB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: toB64url(r.clientDataJSON),
      authenticatorData: toB64url(r.authenticatorData),
      signature: toB64url(r.signature),
      userHandle: toB64url(r.userHandle),
    },
  };
}

/** A name for a new passkey from the device it is made on ("Mac", "iPhone", …). */
export function deviceName(ua = navigator.userAgent): string {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/CrOS/.test(ua)) return 'Chromebook';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Passkey';
}

/** Adds a passkey for the signed-in admin; throws a cancel (see isPasskeyCancel) when they close the prompt. */
export async function addPasskey(name = deviceName()): Promise<AdminPasskeyResponseDTO | null> {
  const opts = unwrap(await adminPasskeyController.registrationOptions({}));
  const cred = (await navigator.credentials.create(creationOptions(opts?.options))) as PublicKeyCredential | null;
  if (!cred) throw new DOMException('cancelled', 'NotAllowedError');
  return unwrap(await adminPasskeyController.register({ request: { challengeId: opts?.challengeId, name, credential: attestationJSON(cred) } }));
}

/** Signs in with a passkey this browser offers; throws a cancel when the prompt is closed. */
export async function signInWithPasskey(): Promise<TokenDTO | null> {
  const opts = unwrap(await adminPasskeyAuthController.options({}));
  const cred = (await navigator.credentials.get(requestOptions(opts?.options))) as PublicKeyCredential | null;
  if (!cred) throw new DOMException('cancelled', 'NotAllowedError');
  return unwrap(await adminPasskeyAuthController.verify({ request: { challengeId: opts?.challengeId, credential: assertionJSON(cred) } }));
}

/** The gateway refuses adding a passkey on a session older than a few minutes (BizError code). */
export const STALE_SIGN_IN = 3006;
